import React from "react";
import {Navigate} from "react-router-dom";

export const ADMIN_PERMISSIONS = {
  wildcard: "*",
  dashboard: "admin.dashboard.view",
  transactions: "payment.transactions.view",
  bills: "payment.bills.view",
  lenco: "payment.lenco.view",
  groupSavingsReports: "group-savings.reports.view",
  groupSavingsLiquidationView: "group-savings.liquidation.view",
  groupSavingsLiquidationApprove: "group-savings.liquidation.approve",
  paymentLinkReports: "payment-links.reports.view",
  paymentLinkRepush: "payment-links.payments.repush",
  paymentLinkLiquidationView: "payment-links.liquidation.view",
  paymentLinkLiquidationApprove: "payment-links.liquidation.approve",
  documentsUpload: "file.document.upload",
  adminUsers: "admin.users.manage",
  supportTicketsView: "support.tickets.view",
  supportTicketsManage: "support.tickets.manage",
  merchantsManage: "merchants.manage",
  merchantTransactionsView: "merchants.transactions.view",
  auditTrail: "audit-trail.view",
  usersManage: "users.manage",
  corporateAccountsManage: "corporate-accounts.manage",
  investmentsView: "investments.view",
  investmentsManage: "investments.manage",
};

export const isSuperAdminRole = (roles?: string[] | null) =>
  (roles || []).some((role) => String(role).toLowerCase() === "superuser");

export const hasAdminPermission = (
  permissions?: string[] | null,
  roles?: string[] | null,
  required?: string | string[]
) => {
  if (!required || (Array.isArray(required) && required.length === 0)) {
    return true;
  }

  if (isSuperAdminRole(roles)) {
    return true;
  }

  const permissionSet = new Set((permissions || []).map((permission) => String(permission).toLowerCase()));
  if (permissionSet.has(ADMIN_PERMISSIONS.wildcard)) {
    return true;
  }

  const requiredPermissions = Array.isArray(required) ? required : [required];
  return requiredPermissions.some((permission) => permissionSet.has(permission.toLowerCase()));
};

export const defaultAuthorizedPath = (permissions?: string[] | null, roles?: string[] | null) => {
  if (hasAdminPermission(permissions, roles, ADMIN_PERMISSIONS.dashboard)) {
    return "/";
  }

  const route = ADMIN_ROUTE_PERMISSIONS.find((item) => hasAdminPermission(permissions, roles, item.permissions));
  return route?.path || "/unauthorized";
};

export const PermissionRoute = ({
  children,
  permissions,
  roles,
  required,
}: {
  children: React.ReactElement;
  permissions?: string[] | null;
  roles?: string[] | null;
  required?: string | string[];
}) => {
  return hasAdminPermission(permissions, roles, required)
    ? children
    : <Navigate to={defaultAuthorizedPath(permissions, roles)} replace/>;
};

export const ADMIN_ROUTE_PERMISSIONS: Array<{path: string; permissions: string | string[]}> = [
  {path: "/", permissions: ADMIN_PERMISSIONS.dashboard},
  {path: "/transactions", permissions: ADMIN_PERMISSIONS.transactions},
  {path: "/lenco-transactions", permissions: ADMIN_PERMISSIONS.lenco},
  {path: "/requests", permissions: ADMIN_PERMISSIONS.groupSavingsLiquidationView},
  {path: "/saving-groups", permissions: ADMIN_PERMISSIONS.groupSavingsReports},
  {path: "/payment-link-reports", permissions: ADMIN_PERMISSIONS.paymentLinkReports},
  {path: "/payment-link-liquidations", permissions: ADMIN_PERMISSIONS.paymentLinkLiquidationView},
  {path: "/payment-link-refunds", permissions: ADMIN_PERMISSIONS.paymentLinkLiquidationView},
  {path: "/bill-payments", permissions: ADMIN_PERMISSIONS.bills},
  {path: "/support-tickets", permissions: ADMIN_PERMISSIONS.supportTicketsView},
  {path: "/admin-users", permissions: ADMIN_PERMISSIONS.adminUsers},
  {path: "/users", permissions: ADMIN_PERMISSIONS.usersManage},
  {path: "/corporate-accounts", permissions: ADMIN_PERMISSIONS.corporateAccountsManage},
  {path: "/merchants", permissions: [ADMIN_PERMISSIONS.merchantsManage, ADMIN_PERMISSIONS.merchantTransactionsView]},
  {path: "/investments", permissions: ADMIN_PERMISSIONS.investmentsView},
  {path: "/corporate-withdrawals", permissions: ADMIN_PERMISSIONS.groupSavingsLiquidationView},
  {path: "/audit-trails", permissions: ADMIN_PERMISSIONS.auditTrail},
  {path: "/admin/approve-liquidation", permissions: ADMIN_PERMISSIONS.groupSavingsLiquidationApprove},
  {path: "/admin/manual-approve-liquidation", permissions: ADMIN_PERMISSIONS.groupSavingsLiquidationApprove},
];
